import {FC, useEffect, useState} from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from '@mui/material';
import {useLazyQuery, useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import Modal from 'components/Modal/Modal';
import {GET_REASSIGNMENT_PROPOSALS} from 'graphql/absences/Querys';
import {APPLY_REASSIGNMENT_BATCH} from 'graphql/absences/Mutations';
import {ReassignmentProposal} from 'models/reassignment';

interface CountryReassignmentModalProps {
  open: boolean;
  countryCode: string;
  onSuccess: () => void;
}

/**
 * Non-dismissible modal that manages palowan reassignment when a country is disabled.
 * Fetches reassignment proposals on mount and allows the admin to select which users
 * to reassign before applying the batch mutation.
 *
 * @param open - Whether the modal is visible.
 * @param countryCode - ISO code of the disabled country whose palowans need reassignment.
 * @param onSuccess - Callback invoked after a successful reassignment batch.
 */
const CountryReassignmentModal: FC<CountryReassignmentModalProps> = ({
  open,
  countryCode,
  onSuccess,
}) => {
  const {t} = useTranslation();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fetchProposals, {data, loading}] = useLazyQuery(
    GET_REASSIGNMENT_PROPOSALS
  );

  const [applyReassignmentBatch] = useMutation(APPLY_REASSIGNMENT_BATCH);

  useEffect(() => {
    if (open) {
      setSelectedUserIds([]);
      setErrorMessage(null);
      fetchProposals({variables: {countryCode}});
    }
  }, [open, countryCode, fetchProposals]);

  const proposals: ReassignmentProposal[] =
    data?.getReassignmentProposals?.proposals ?? [];

  const allSelected =
    proposals.length > 0 && selectedUserIds.length === proposals.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds(proposals.map(p => p.userId));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Derive the target country from the first suggestion with priority 1 of the first proposal
  const getTargetCountryCode = (): string => {
    if (proposals.length === 0) return '';
    const first = proposals[0];
    const topSuggestion = first.suggestedCountries.find(s => s.priority === 1);
    return (
      topSuggestion?.countryCode ??
      first.suggestedCountries[0]?.countryCode ??
      ''
    );
  };

  const handleApply = async () => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await applyReassignmentBatch({
        variables: {
          userIds: selectedUserIds,
          newCountryCode: getTargetCountryCode(),
        },
      });
      onSuccess();
    } catch {
      setErrorMessage(t('leaves.CountryReassignmentModal.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('leaves.CountryReassignmentModal.title')}
      description={t('leaves.CountryReassignmentModal.description')}
      onClose={() => {}}
      disableEscapeKeyDown
      small
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <CircularProgress />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={selectedUserIds.length > 0 && !allSelected}
                onChange={e => handleSelectAll(e.target.checked)}
              />
            }
            label={t('leaves.CountryReassignmentModal.selectAll')}
          />
          {proposals.map(proposal => (
            <FormControlLabel
              key={proposal.userId}
              control={
                <Checkbox
                  checked={selectedUserIds.includes(proposal.userId)}
                  onChange={() => handleToggleUser(proposal.userId)}
                />
              }
              label={`${proposal.userName} (${proposal.userEmail})`}
            />
          ))}
          {errorMessage && (
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          )}
          <div className="flex justify-end">
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={selectedUserIds.length === 0 || isSubmitting}
            >
              {t('leaves.CountryReassignmentModal.applyButton')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CountryReassignmentModal;
